import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import executionStatusColor from '../../utils/executionStatusColors';
import { Bar, StatusTags } from './style';

// Order the health bar draws its segments in: successes, then warnings
// (errors / circuit-breaker), then blocked-by-condition, running, and failures last.
export const SEGMENT_ORDER = [
  'SUCCEEDED',
  'NOT_FOUND',
  'COMPLETED_WITH_ERRORS',
  'BLOCKED_BY_CIRCUIT_BREAKER',
  'RE_ENQUEUED_BY_CIRCUIT_BREAKER',
  'BLOCKED_BY_CONDITION_RULE',
  'RUNNING',
  'FAILED',
];

// Semantic ui-components color -> the health page's CSS custom properties
// (fill for bar segments / dots, ink for count text).
const VAR = {
  success: { fill: 'var(--good-bright)', ink: 'var(--good)' },
  danger: { fill: 'var(--danger-bright)', ink: 'var(--danger)' },
  warning: { fill: 'var(--warn-bright)', ink: 'var(--warn)' },
  information: { fill: 'var(--info-bright)', ink: 'var(--info)' },
};

// Text color (CSS var) for a given execution status — matches the badges.
export const statusInk = (status) => VAR[executionStatusColor(status)].ink;

// Labels for every SEGMENT_ORDER status (that list is the single source of keys).
const shortLabel = (status) =>
  ({
    SUCCEEDED: t`Succeeded`,
    NOT_FOUND: t`No results`,
    COMPLETED_WITH_ERRORS: t`Errors`,
    BLOCKED_BY_CIRCUIT_BREAKER: t`Circuit breaker`,
    RE_ENQUEUED_BY_CIRCUIT_BREAKER: t`Re-enqueued`,
    BLOCKED_BY_CONDITION_RULE: t`Blocked`,
    RUNNING: t`Running`,
    FAILED: t`Failed`,
  }[status]);

// Ordered, non-zero per-status segments from the API's by_status map. Falls back to the
// legacy succeeded/failed/blocked scalars when the backend hasn't shipped by_status yet.
export const segmentsFor = (a = {}) => {
  if (a.by_status) {
    return SEGMENT_ORDER.map((status) => {
      const c = VAR[executionStatusColor(status)];
      return {
        key: status,
        label: shortLabel(status),
        count: a.by_status[status] || 0,
        fill: c.fill,
        ink: c.ink,
      };
    }).filter((s) => s.count > 0);
  }
  return [
    {
      key: 'SUCCEEDED',
      label: t`Succeeded`,
      count: a.succeeded || 0,
      ...VAR.success,
    },
    { key: 'FAILED', label: t`Failed`, count: a.failed || 0, ...VAR.danger },
    {
      key: 'blocked',
      label: t`Blocked`,
      count: a.blocked || 0,
      ...VAR.warning,
    },
  ].filter((s) => s.count > 0);
};

const attributesShape = PropTypes.shape({
  succeeded: PropTypes.number,
  failed: PropTypes.number,
  blocked: PropTypes.number,
  by_status: PropTypes.objectOf(PropTypes.number),
});

export function HealthBar({ attributes }) {
  const segs = segmentsFor(attributes);
  const total = segs.reduce((n, s) => n + s.count, 0) || 1;
  return (
    <Bar>
      {segs.map((s) => (
        <i
          key={s.key}
          style={{ width: `${(s.count / total) * 100}%`, background: s.fill }}
          title={`${s.label}: ${s.count}`}
        />
      ))}
    </Bar>
  );
}
HealthBar.propTypes = { attributes: attributesShape.isRequired };

export function StatusCounts({ attributes }) {
  const segs = segmentsFor(attributes);
  if (!segs.length)
    return (
      <StatusTags className="empty">{t`No executions in the window`}</StatusTags>
    );
  return (
    <StatusTags>
      {segs.map((s) => (
        <span key={s.key} style={{ color: s.ink }}>
          <i style={{ background: s.fill }} />
          <b>{s.count}</b> {s.label}
        </span>
      ))}
    </StatusTags>
  );
}
StatusCounts.propTypes = { attributes: attributesShape.isRequired };
